// @flow

import * as React from 'react';
import styled from 'styled-components';
import sendJIEvent from '../lib/kwizzad-ji';
import includes from 'lodash/includes';
import type { Campaign } from '../lib/Campaign';
import type { Reengagement, ReengagementPlan } from '../lib/Reengagement';
import {
    reengagementPlan,
    totalPossiblePointsFor,
    earnedPointsFor,
    completionStateFor
} from '../lib/Reengagement';
import createTVSImageId from '../lib/cdn-image-urls';
import SmilesIcon from './SmilesIcon';
import ExpandTriangleIcon from './ExpandTriangleIcon';
import LiveComponent from './LiveComponent';
import type { LiveComponentState } from './LiveComponent';
import ReengagementDay from './ReengagementDay';

type Props = {
    campaign: Campaign,
    reengagement: ?Reengagement,
    className: string,
};

type State = LiveComponentState & {
    collapsed: boolean,
}


class CampaignOverview extends LiveComponent<Props, State> {
    state = {
        collapsed: true,
    };

    nextStepIndex(plan: ReengagementPlan, reengagement: Reengagement) {
        const days = Object.keys(plan);
        return days.findIndex((day, dayIndexInPlan) => {
            const state = completionStateFor(reengagement, Number(day), dayIndexInPlan);
            return includes(['today', 'future'], state);
        });
    }

    toggle() {
        this.setState({ collapsed: !this.state.collapsed });
    }

    render() {
        const { campaign } = this.props;
        const challenges = campaign.challenges;
        const reengagement = this.props.reengagement;
        if (!reengagement) return null;

        const plan = reengagementPlan(challenges);
        const nextStepIndex = this.nextStepIndex(plan, reengagement);

        const installationChallenge = challenges.find(challenge => challenge.challengeType === 'web');
        const installationChallengeJSON = installationChallenge && JSON.stringify(installationChallenge);
        const onStartChallenge = (event) => {
            event.stopPropagation();
            sendJIEvent('nativeChallenge', installationChallengeJSON);
        };

        const totalPossiblePoints = totalPossiblePointsFor(plan);
        const earnedPoints = earnedPointsFor(plan, reengagement);
        const earnedPointsString = Number(earnedPoints).toLocaleString();
        const totalPossiblePointsString = Number(totalPossiblePoints).toLocaleString();
        const progress = earnedPoints / totalPossiblePoints;
        const className = [
            this.props.className,
            'has-todo',
            this.state.collapsed ? 'collapsed' : 'expanded',
        ].filter(Boolean).join(' ');

        return (<li className={className} onClick={(event) => {
            if (event.target.tagName !== 'BUTTON') {
                this.toggle();
            }
        }}>
            <header>
                <figure className="campaign-image">
                    <img alt="" src={createTVSImageId(campaign.campaignImage, 'campaigns')} />
                </figure>
                <section className="overview">
                    <header>
                        <h2>{campaign.brandName}</h2>
                        <header className="result">
                            <span className="earned">{earnedPointsString}</span>
                            ∕
                            <span className="total">{totalPossiblePointsString}</span>
                            &nbsp;
                            <SmilesIcon />
                        </header>
                    </header>
                    <h3>{campaign.headline}</h3>
                </section>
            </header>

            <section className="history">
                {Object.keys(plan)
                    .map((day, index) => <ReengagementDay
                        key={day}
                        day={Number(day)}
                        dayIndexInPlan={index}
                        isNextStep={index === nextStepIndex}
                        awardedPoints={plan[Number(day)]}
                        campaign={this.props.campaign}
                        reengagement={this.props.reengagement}
                        onStartChallenge={onStartChallenge}
                        lastFullSecondTimestamp={this.state.lastFullSecondTimestamp}
                    />)}
            </section>

            <section className="progress">
                <div className="bar" style={{ width: `${progress * 100}%`}}></div>
            </section>

            <button className="expand-button"><ExpandTriangleIcon /></button>
        </li>);
    }
}

export default styled(CampaignOverview)`
    display: flex;
    flex-direction: column;
    position: relative;

    background-color: #efefef;
    box-shadow: 0 1px 15px rgba(0, 0, 0, 0.5);
    transition: background-color 0.3s ease-out;

    > .progress {
        height: 6px;
        .bar {
            transition: width 0.3s ease-out;
            background-color: #17b3ee;
            height: 6px;
            opacity: 0.5;
        }
    }

    > .history {
        background-color: #efefef;
        background: linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.1) 6%, rgba(0, 0, 0, 0.0));
        background-size: 100% 60px;
        background-position: top;
        background-repeat: no-repeat;
        padding-top: 6px;
        transition: background-color 0.3s ease-out;
        pointer-events: none;

        > .step {
            box-sizing: border-box;
            transition: max-height 0.3s ease-out, opacity 0.3s ease-out, background-color 0.3s ease-out;
            &:not(.next-step) {
                max-height: 0;
                opacity: 0;
            }
        }
    }

    > header {
        display: flex;
        flex-direction: row;
        background-color: white;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);

        .campaign-image {
            width: 100px;
            height: 100px;
            img {
                width: 100px;
                height: 100px;
                content-fit: contain;
            }
        }

        > .overview {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            justify-content: space-evenly;
            padding: 5px 8px;

            > header {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
            }

            .result {
                /* font-size: 25px; */
                font-weight: 200;
                opacity: 0.5;
                .earned {
                    /* vertical-align: 0.05em; */
                    margin-right: 0.2em;
                }
                .total {
                    /* vertical-align: -0.05em; */
                    margin-left: 0.2em;
                }
            }
        }

        h2, h3 {
            display: inline;
            font-size: 1rem;
        }

        h2 {
            font-weight: normal;
        }
    }

    > .expand-button {
        position: absolute;
        top: 0px;
        left: 0px;
        background-color: transparent;
        border: none;
        border-radius: none;
        width: 30px;
        height: 30px;
        transition: transform 0.15s ease-out;
        transform: rotateZ(0deg);
        opacity: 0.4;
        pointer-events: none;
    }


    &.has-todo.collapsed {
        background-color: #ebf6ff;
        > .history {
            > .step {
                background-color: transparent;
            }
        }
    }

    &.expanded {
        > .history {
            .step {
                max-height: 60px;
                opacity: 1;
            }
        }

        > .expand-button {
            transform: rotateZ(90deg);
        }
    }
`;


// WEBPACK FOOTER //
// ./src/Reengagement/CampaignOverview.jsx